import {
  ContactsOutlined,
  HistoryOutlined,
  LeftOutlined,
  ProjectOutlined,
  RedoOutlined,
  RightOutlined,
  ScheduleOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Button, Menu } from "antd";
import React, { useState } from "react";

import {
  CloudOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  IssuesCloseOutlined,
  NodeExpandOutlined,
  OrderedListOutlined,
  PullRequestOutlined,
  SettingOutlined,
  TruckOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { DepartmentCode, PositionCode, UserRole } from "../../enum/roles";
import { RootState } from "../../store";
import MenuItem from "../common/MenuItem";
import AirplaneIcon from "../icons/AirplaneIcon";
import BillIcon from "../icons/BillIcon";
import BuyIcon from "../icons/BuyIcon";
import CheckAllIcon from "../icons/CheckAll";
import DepartmentIcon from "../icons/DepartmentIcon";
import EmployeeIcon from "../icons/EmployeeIcon";
import FormIcon from "../icons/FormIcon";
import GoodsSearchIcon from "../icons/GoodsSearch";
import LeaveRequestIcon from "../icons/LeaveRequestIcon";
import { MeetingIcon } from "../icons/MeetingIcon";
import OvertimeIcon from "../icons/OvertimeIcon";
import PartnerIcon from "../icons/PartnerIcon";
import ProductionIcon from "../icons/Production";
import ResearchIcon from "../icons/ResearchIcon";
import SalesIcon from "../icons/SalesIcon";
import ShieldIcon from "../icons/ShieldIcon";
import ShipmentsIcon from "../icons/ShipmentIcon";
import ShoppingFavouriteIcon from "../icons/ShoppingFavourite";
import { ShoppingPlusIcon } from "../icons/ShoppingPlusIcon";
import StaionerytIcon from "../icons/StaioneryIcon";
import TaxIcon from "../icons/TaxIcon";
import WarehouseIcon from "../icons/WarehouseIcon";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const role = user?.user?.role;
  const departmentCode = user?.user?.department?.departmentCode;
  const positionCode = user?.user?.position?.positionCode;
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar");
    return saved === "true";
  });
  const { theme } = useTheme();

  const findSelectedKey = (currentPath: string, items: any[]): string => {
    const keys: string[] = [];
    function extractKeys(menuItems: any[]) {
      menuItems.forEach((item) => {
        if (item.key) keys.push(item.key);
        if (item.children) extractKeys(item.children);
      });
    }
    extractKeys(items);
    return (
      keys
        .filter((key) => currentPath.startsWith(key))
        .sort((a, b) => b.length - a.length)[0] || currentPath
    );
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebar", String(newState));
      return newState;
    });
  };

  const hasAccess = (
    allowedRoles: string[] = [],
    allowedDepartments: string[] = [],
    allowedPositions: string[] = [],
  ) => {
    if (user?.user?.role === UserRole.SUPER_ADMIN) return true;
    if (user?.user?.role === UserRole.ADMIN) {
      if (allowedRoles.length > 0) {
        return allowedRoles.includes(UserRole.ADMIN);
      }
      return true;
    }
    if (
      !allowedRoles.length &&
      !allowedDepartments.length &&
      !allowedPositions.length
    )
      return true;

    return (
      (!allowedRoles.length || (role && allowedRoles.includes(role))) &&
      (!allowedDepartments.length ||
        (departmentCode && allowedDepartments.includes(departmentCode))) &&
      (!allowedPositions.length ||
        (positionCode && allowedPositions.includes(positionCode)))
    );
  };

  const filterMenuItems = (items: any[]): any[] => {
    return items
      .filter((item) => {
        const { allowedRoles, allowedDepartments, allowedPositions } = item;
        return hasAccess(allowedRoles, allowedDepartments, allowedPositions);
      })
      .map((item) => {
        const { allowedRoles, allowedDepartments, allowedPositions, ...rest } =
          item;
        return {
          ...rest,
          children: item.children ? filterMenuItems(item.children) : undefined,
        };
      })
      .filter((item) => !item.children || item.children.length > 0);
  };

  const allMenuItems = [
    MenuItem({
      label: "Trang chủ",
      key: "/dashboard",
      icon: <HomeOutlined />,
    }),
    MenuItem({
      label: "Thông tin cá nhân",
      key: "/personal",
      icon: <UserOutlined />,
    }),
    MenuItem({
      label: "Cài đặt",
      key: "/setting",
      icon: <SettingOutlined />,
      allowedRoles: [UserRole.SUPER_ADMIN],
      children: [
        MenuItem({
          label: "Tài khoản hệ thống",
          key: "/setting/accounts",
          icon: <UserOutlined />,
          allowedRoles: [UserRole.SUPER_ADMIN],
        }),
        MenuItem({
          label: "Quyền truy cập",
          key: "/setting/permissions",
          icon: <ShieldIcon />,
          allowedRoles: [UserRole.SUPER_ADMIN],
        }),
        MenuItem({
          label: "Phòng ban",
          key: "/setting/departments",
          icon: <DepartmentIcon />,
          allowedRoles: [UserRole.SUPER_ADMIN],
        }),
      ],
    }),
    MenuItem({
      label: "Kho",
      key: "/warehouse/all",
      icon: <WarehouseIcon />,
      allowedDepartments: [DepartmentCode.WH],
      children: [
        MenuItem({
          label: "Tổng kho",
          key: "/warehouse",
          icon: <CloudOutlined />,
          allowedDepartments: [DepartmentCode.WH],
        }),
        MenuItem({
          label: "Xuất kho",
          key: "/warehouse/exports",
          icon: <NodeExpandOutlined />,
          allowedDepartments: [DepartmentCode.WH],
        }),
        MenuItem({
          label: "Danh sách nhập/xuất",
          key: "/warehouse/list-ex-im",
          icon: <OrderedListOutlined />,
          allowedDepartments: [DepartmentCode.WH],
        }),
        MenuItem({
          label: "Vị trí kho",
          key: "/warehouse/locations",
          icon: <EnvironmentOutlined />,
          allowedDepartments: [DepartmentCode.WH],
        }),
      ],
    }),
    MenuItem({
      label: "Đánh giá chất lượng",
      key: "/quality-control",
      icon: <CheckAllIcon />,
      allowedDepartments: [DepartmentCode.QC],
      children: [
        MenuItem({
          label: "Yêu cầu QC",
          key: "/quality-control/qc-requests",
          icon: <PullRequestOutlined />,
          allowedDepartments: [DepartmentCode.QC],
        }),
        MenuItem({
          label: "Danh sách kết quả QC",
          key: "/quality-control/qc-issues",
          icon: <IssuesCloseOutlined />,
          allowedDepartments: [DepartmentCode.QC],
        }),
        MenuItem({
          label: "Trả hàng",
          key: "/quality-control/returns",
          icon: <RedoOutlined />,
          allowedDepartments: [DepartmentCode.QC],
        }),
      ],
    }),
    MenuItem({
      label: "Sản xuất",
      key: "/production",
      icon: <ProductionIcon />,
      allowedDepartments: [DepartmentCode.PD],
      children: [
        MenuItem({
          label: "Lịch làm việc",
          key: "/production/timesheet",
          icon: <ScheduleOutlined />,
          allowedDepartments: [DepartmentCode.PD],
        }),
        MenuItem({
          label: "Danh sách dự án",
          key: "/production/project",
          icon: <ProjectOutlined />,
          allowedDepartments: [DepartmentCode.PD],
        }),
      ],
    }),
    MenuItem({
      label: "Nghiên cứu & phát triển",
      key: "/r&d",
      icon: <ResearchIcon />,
      allowedDepartments: [DepartmentCode.RND],
      children: [
        MenuItem({
          label: "Danh sách dự án",
          key: "/r&d/projects",
          icon: <ProjectOutlined />,
          allowedDepartments: [DepartmentCode.RND],
        }),
      ],
    }),
    MenuItem({
      label: "Mua hàng",
      key: "/purchase",
      icon: <ShoppingOutlined />,
      allowedDepartments: [DepartmentCode.PUR],
      children: [
        MenuItem({
          label: "Nhà cung cấp",
          key: "/purchase/suppliers",
          icon: <PartnerIcon />,
        }),
        MenuItem({
          label: "Yêu cầu mua hàng",
          key: "/purchase/requests",
          icon: <ShoppingPlusIcon />,
          allowedDepartments: [DepartmentCode.PUR],
          allowedPositions: [PositionCode.PUR_MANAGER],
        }),
        MenuItem({
          label: "Đơn mua của tôi",
          key: "/purchase/request-assign",
          icon: <ShoppingFavouriteIcon />,
          allowedDepartments: [DepartmentCode.PUR],
        }),
        MenuItem({
          label: "Đơn mua hàng",
          key: "/purchase/orders",
          icon: <BuyIcon />,
          allowedDepartments: [DepartmentCode.PUR],
        }),
        // MenuItem({
        //   label: "Danh sách đơn nhận",
        //   key: "/purchase/receipts",
        //   icon: <ReceiptIcon />,
        //   allowedDepartments: [DepartmentCode.PUR],
        // }),
      ],
    }),
    MenuItem({
      label: "Bán hàng",
      key: "/sales",
      icon: <SalesIcon />,
      children: [
        MenuItem({
          label: "Danh mục khách hàng",
          key: "/sales/customers",
          icon: <UserOutlined />,
        }),
        MenuItem({
          label: "Danh sách liên hệ",
          key: "/sales/contacts",
          icon: <ContactsOutlined />,
        }),
      ],
    }),
    MenuItem({
      label: "Lịch sử truy vết",
      key: "/history",
      icon: <HistoryOutlined />,
      children: [
        MenuItem({
          label: "Truy vết mã hàng",
          key: "/history/part-number",
          icon: <GoodsSearchIcon />,
        }),
      ],
    }),
    MenuItem({
      label: "Quản lý mã vận đơn",
      key: "/awbs",
      icon: <BillIcon />,
    }),
    MenuItem({
      label: "Logistics",
      key: "/logistics",
      icon: <AirplaneIcon />,
      children: [
        MenuItem({
          label: "Quản lý thông quan",
          key: "/logistics/customs-clearance",
          icon: <ShipmentsIcon />,
          allowedDepartments: [DepartmentCode.LOG],
        }),
        MenuItem({
          label: "Thuế vật tư nhập khẩu",
          key: "/logistics/import-taxes",
          icon: <TaxIcon />,
          allowedDepartments: [DepartmentCode.LOG],
        }),
        MenuItem({
          label: "Tracking đơn hàng",
          key: "/logistics/tracking",
          icon: <TruckOutlined />,
        }),
        MenuItem({
          label: "Tracking list",
          key: "/logistics/tracking-list",
          icon: <UnorderedListOutlined />,
        }),
      ],
    }),
    MenuItem({
      label: "Quản lý nhân sự",
      key: "/hrm",
      icon: <TeamOutlined />,
      children: [
        MenuItem({
          label: "Quản lý nhân viên",
          key: "/hrm/employee",
          icon: <EmployeeIcon />,
          allowedDepartments: [DepartmentCode.HR],
        }),
        MenuItem({
          label: "Quản lý nghỉ phép",
          key: "/hrm/leave-requests",
          icon: <LeaveRequestIcon />,
          allowedDepartments: [DepartmentCode.HR],
        }),
        MenuItem({
          label: "Quản lý tăng ca",
          key: "/hrm/overtime-requests",
          icon: <OvertimeIcon />,
          allowedDepartments: [DepartmentCode.HR],
        }),
        MenuItem({
          label: "Quản lý văn phòng phẩm",
          key: "/hrm/stationerys",
          icon: <StaionerytIcon />,
          allowedDepartments: [DepartmentCode.HR],
        }),
        MenuItem({
          label: "Quản lý lịch họp",
          key: "/hrm/meeting-schedules",
          icon: <MeetingIcon />,
          allowedDepartments: [DepartmentCode.HR],
        }),
      ],
    }),
    MenuItem({
      label: "Biểu mẫu",
      key: "/form",
      icon: <FormIcon />,
    }),
  ];

  const menuItems = filterMenuItems(allMenuItems);

  return (
    <div
      className={`h-full flex-shrink-0 shadow-lg transition-colors duration-300 relative border-r flex flex-col ${
        collapsed ? "w-20" : "w-64"
      }`}
      style={{
        backgroundColor: "var(--bg-primary)",
        transition: "width 0.3s ease-in-out",
        borderRightColor: "var(--border-color)",
      }}
    >
      <img
        src="/logo.svg"
        alt="Trace-Tec"
        className={`mx-auto ${
          collapsed ? "w-16 h-20" : "w-26 h-20"
        } flex-shrink-0`}
        style={{
          transition: "width 0.3s ease-in-out",
        }}
        onClick={() => navigate("/dashboard")}
      />
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={
          {
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          } as any
        }
      >
        <Menu
          theme={theme === "dark" ? "dark" : "light"}
          style={{
            backgroundColor: "var(--bg-primary)",
            transition: "width 0.3s ease-in-out",
          }}
          className="transition-colors duration-300"
          mode="inline"
          selectedKeys={[findSelectedKey(location.pathname, menuItems)]}
          onClick={({ key }) => navigate(key)}
          items={menuItems}
          inlineCollapsed={collapsed}
        />
      </div>
      <div className="flex-shrink-0 p-2">
        <Button
          type="text"
          className="block mx-auto"
          onClick={toggleCollapse}
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
        />
      </div>
    </div>
  );
};

export default Sidebar;
